import { useTranslation } from "react-i18next";
import type { Permission } from "@astroplanet/shared";
import { useAppState, type TabId } from "../../context/AppStateContext";
import { useAuth } from "../../context/AuthContext";
import { useBookings, useConversations, useSessions } from "../../hooks/queries";
import { useHrMe, useInbox } from "../../hooks/hr";
import { useHardwareSnapshot } from "../../lib/hardwareStore";
import { useFeatures } from "../../hooks/v2";
import { isV2 } from "../../lib/config";
import { CapacityMeter } from "./CapacityMeter";

interface NavItem { id: TabId; icon: string; badge?: string; managerOnly?: boolean; perm?: Permission }

export function Sidebar() {
  const { t } = useTranslation();
  const { tab, setTab } = useAppState();
  const { can } = useAuth();
  const sessions = useSessions();
  const chats = useConversations();
  const bookings = useBookings();
  const features = useFeatures();
  const hr = useHrMe();
  const hw = useHardwareSnapshot();
  const hrInbox = useInbox(!!hr.data?.enabled);

  const awaiting = (chats.data ?? []).filter((c) => c.awaitingReply).length;
  const items: NavItem[] = [
    { id: "cashier", icon: "⏱️", perm: "pos", badge: String(sessions.data?.capacity.occupied ?? 0) },
    { id: "whatsapp", icon: "💬", perm: "whatsapp", badge: awaiting ? t("nav.awaiting", { count: awaiting }) : undefined },
    { id: "parties", icon: "🎉", perm: "parties", badge: t("nav.today", { count: bookings.data?.length ?? 0 }) },
    { id: "loyalty", icon: "⭐", perm: "loyalty" },
    ...(isV2() ? ([{ id: "customers", icon: "👪", perm: "customers" }] as NavItem[]) : []),
    ...(isV2() && features.data?.wallet_enabled ? ([{ id: "wallet", icon: "💳", perm: "wallet" }] as NavItem[]) : []),
    ...(isV2() && hr.data?.enabled && (hr.data.employee || hr.data.level >= 1) ? ([{ id: "hr", icon: "👥", badge: hrInbox.data?.length ? String(hrInbox.data.length) : undefined }] as NavItem[]) : []),
    ...(isV2() ? ([{ id: "hardware", icon: "🖨️", perm: "hardware", badge: hw.failed.length ? String(hw.failed.length) : undefined }] as NavItem[]) : []),
    ...(isV2() ? ([{ id: "pricing", icon: "🏷️", perm: "pricing" }] as NavItem[]) : []),
    ...(isV2() ? ([{ id: "staff", icon: "🔐", perm: "staff" }, { id: "setup", icon: "🧰", perm: "setup" }] as NavItem[]) : []),
    ...(isV2() ? ([{ id: "downloads", icon: "⬇️", perm: "downloads" }] as NavItem[]) : []),
    ...(isV2() ? ([{ id: "reports", icon: "📈", perm: "reports" }, { id: "expenses", icon: "💸", perm: "expenses" }, { id: "receipts", icon: "🧾", perm: "receipts" }, { id: "shifts", icon: "💰", perm: "shifts" }, { id: "einvoice", icon: "🏛️", perm: "einvoice" }, { id: "audit", icon: "🛡️", perm: "audit" }] as NavItem[]) : []),
    { id: "admin", icon: "📊", perm: "admin" },
  ];

  return (
    <aside className="flex w-full flex-shrink-0 justify-between overflow-x-auto border-e border-[#1D2254] bg-cosmic-navy p-3 text-white md:w-64 md:flex-col md:overflow-x-visible md:p-4" aria-label={t("nav.label")}>
      <nav className="flex w-full gap-1.5 md:flex-col">
        {items
          .filter((i) => !i.perm || can(i.perm))
          .map((i) => {
            const active = tab === i.id;
            return (
              <button
                key={i.id}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => setTab(i.id)}
                className={`flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-xs font-bold transition ${active ? "bg-cosmic-purple text-white shadow-cosmic" : "text-gray-400 hover:bg-white/10 hover:text-white"}`}
              >
                <span className="text-lg" aria-hidden>{i.icon}</span>
                <span className="flex-1 whitespace-nowrap text-start">{t(`nav.${i.id}`)}</span>
                {i.badge && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/20" : "bg-white/10 text-gray-300"}`}>{i.badge}</span>}
              </button>
            );
          })}
      </nav>
      <CapacityMeter />
    </aside>
  );
}
